/**
 * The keyboard that is just a keyboard: one key, one character.
 *
 * ── Why this is a file and not three lines in the component ─────────────────
 * Because of dead keys, which are the whole of the interesting behaviour here
 * and are not optional. Greek without the tonos key cannot write ά, and ά is
 * in most Greek sentences; the same goes for the caron on several Cyrillic
 * boards and for half of Latin Europe. A "direct" layout that dropped them
 * would type a language missing its accents, which reads as broken text rather
 * than as a missing feature.
 *
 * ── How a dead key works here ───────────────────────────────────────────────
 * Pressing one types NOTHING and arms a combining mark. The next character
 * typed is combined with it and the pair is normalised, so `´` then `α` is one
 * character, `ά`, rather than two. That is what a real dead key does, and it
 * is why the armed state has to live outside this module — between the two
 * keystrokes there is nothing in the text to hold it.
 *
 * `normalize('NFC')` does the work, and it is doing something worth naming: it
 * turns the two-codepoint sequence into the single precomposed character where
 * Unicode has one, and leaves it as a sequence where Unicode does not. Both
 * render identically and both are correct input; the precomposed form is
 * simply the one every other tool will produce, so text typed here matches
 * text typed anywhere else.
 */

import type { ImeEdit } from './keyboards';
import type { GeneratedKey, GeneratedLayout } from './keyboardLayouts.generated';

/** What one keypress produces: a character, or a mark to hold for the next one. */
export interface DirectPress {
  edit: ImeEdit | null;
  /** The combining mark now armed, or null. Replaces whatever was armed. */
  dead: string | null;
}

/** The cap a physical key lands on, or null when the layout leaves it alone. */
export function capFor(layout: GeneratedLayout, key: string): GeneratedKey | null {
  const lower = key.toLowerCase();
  for (const row of [...layout.rows, layout.digits ?? []]) {
    for (const cap of row) if (cap.key === lower) return cap;
  }
  return null;
}

/**
 * What a cap types, given whether Shift is down.
 *
 * Returns null when the cap has no shifted form and Shift is held — which is
 * NOT the same as "types the unshifted character". A layout that leaves a key
 * alone at level 2 means the physical keyboard's own shifted character should
 * come through, and swallowing it would eat every capital letter and every
 * `!` on a board that does not redefine them.
 */
export function outputOf(cap: GeneratedKey, shift: boolean): { char: string; dead: boolean } | null {
  if (shift) {
    if (!cap.shift) return null;
    return { char: cap.shift, dead: !!(cap.dead && cap.dead & 2) };
  }
  return { char: cap.base, dead: !!(cap.dead && cap.dead & 1) };
}

/**
 * One character, landing on whatever is in front of the caret.
 *
 * `armed` is the mark a dead key left behind, if any. Note what happens when a
 * dead key is pressed twice, or when one is armed and another arrives: the new
 * one replaces the old. That is the common convention and the only one that
 * can be undone by a reader who pressed the wrong accent — the alternative,
 * stacking them, produces a character nobody asked for and no way back to a
 * plain letter except backspace.
 */
export function typeDirect(char: string, dead: boolean, armed: string | null): DirectPress {
  if (dead) return { edit: null, dead: char };
  if (armed) return { edit: { back: 0, insert: (char + armed).normalize('NFC') }, dead: null };
  return { edit: { back: 0, insert: char }, dead: null };
}

/**
 * A combining mark, drawn so it can be seen.
 *
 * U+25CC DOTTED CIRCLE is the character Unicode provides for exactly this: a
 * placeholder base for showing a mark on its own. Without it the cap renders
 * the accent on top of whatever glyph the font puts first, or on nothing at
 * all, and half the dead keys on the Greek board come out blank.
 */
export function showMark(mark: string): string {
  return `◌${mark}`;
}

/** Whether a character is a combining mark, and therefore needs the circle. */
export function isMark(ch: string): boolean {
  return /^\p{M}$/u.test(ch);
}

/**
 * Characters with no glyph, named so the cap is not blank.
 *
 * Malayalam's layout really does carry ZWJ and ZWNJ — they are how chillu
 * letters are typed, so they cannot be dropped — and both render as nothing at
 * all. A cap showing nothing reads as a broken layout; a cap saying ZWJ reads
 * as a key for people who know what it is for, which is exactly who presses it.
 */
const INVISIBLE: Record<string, string> = {
  '​': 'ZWSP',
  '‌': 'ZWNJ',
  '‍': 'ZWJ',
  ' ': 'NBSP',
  '؜': 'ALM',
  '‎': 'LRM',
  '‏': 'RLM',
};

/** What to draw on a cap: the character, a marked circle, or a short name. */
export function capLabel(char: string): string {
  if (INVISIBLE[char]) return INVISIBLE[char];
  if (isMark(char)) return showMark(char);
  return char;
}

/**
 * A US shifted character → the key it is printed on.
 *
 * Needed because a layout is indexed by the UNSHIFTED key — `1`, `;`, `/` —
 * while a keydown event reports what the key PRODUCED, which with Shift held
 * is `!`, `:` or `?`. Without this table, Shift plus any non-letter key found
 * no cap and fell through to the browser, so the shifted half of every
 * punctuation key on every layout was unreachable: no Thai ฿, no Arabic ؟, no
 * Greek colon.
 *
 * Letters need no entry — `String.toLowerCase` is the whole mapping.
 */
const US_SHIFTED: Record<string, string> = {
  '~': '`', '!': '1', '@': '2', '#': '3', $: '4', '%': '5', '^': '6', '&': '7',
  '*': '8', '(': '9', ')': '0', _: '-', '+': '=', '{': '[', '}': ']', '|': '\\',
  ':': ';', '"': "'", '<': ',', '>': '.', '?': '/',
};

/**
 * Which key on the board a keydown landed on, ignoring what it produced.
 *
 * `event.key` rather than `event.code`, deliberately, and the difference
 * matters for exactly one group of people: somebody on an AZERTY or Dvorak
 * keyboard. `code` is the physical position on a US board, so it would map
 * their `A` to wherever `Q` sits on ours; `key` is the letter printed on the
 * cap in front of them, which is the thing the panel is teaching them to
 * associate with a Cyrillic letter. The Korean keyboard made this choice
 * first, and the two must not disagree.
 *
 * Returns null for anything that is not a single character — Enter, Tab, the
 * arrow keys — which is how every one of those keeps doing its own job.
 */
export function physicalKey(key: string): string | null {
  if (key.length !== 1) return null;
  const lower = key.toLowerCase();
  if (lower !== key) return lower;            // a capital letter
  return US_SHIFTED[key] ?? key;
}
